// store/slices/orderSlice.js - FIXED VERSION
// Changes:
// 1. Removed duplicate admin fulfillment thunk imports
// 2. Removed duplicate builder cases (saves ~100 lines of code)
// 3. All functionality preserved - admin uses the regular fulfillment thunks

import { createSlice } from "@reduxjs/toolkit";
import {
  // Admin thunks - Creation
  createOrderFromSessionThunk,
  createDirectOrderThunk,
  createOrderFromCartThunk,

  // Admin thunks - Editing
  getOrderDraftThunk,
  updateOrderItemsThunk,

  // Admin thunks - Pricing & Adjustments
  calculateOrderPricingThunk,
  addPriceAdjustmentThunk,
  removePriceAdjustmentThunk,
  approvePriceAdjustmentThunk,
  rejectPriceAdjustmentThunk,
  lockPricesThunk,
  unlockPricesThunk,

  // Admin thunks - Exchange Items
  addExchangeItemThunk,
  adjustExchangeValueThunk,
  acceptExchangeItemThunk,
  editExchangeItemThunk,
  removeExchangeItemThunk,
  unacceptExchangeItemThunk,
  calculateExchangeValueThunk,

  // Admin thunks - Order Notes
  updateOrderNotesThunk,

  // Admin thunks - Approval
  submitForApprovalThunk,
  approveOrderThunk,
  rejectOrderThunk,
  finalizeOrderThunk,

  // Admin thunks - Management
  getAllOrdersForAdminThunk,
  updateOrderStatusThunk,
  updatePaymentStatusThunk,
  getOrderStatsThunk,
  getOrderByIdForAdminThunk,
  cancelOrderThunk,

  // Actions
  clearOrderError,
  clearCurrentOrder,
  clearDraftOrder,
  clearPricingData,
  clearAdjustments,

  // ✅ FULFILLMENT THUNKS - Only one set (admin can use these)
  schedulePickupThunk,
  reschedulePickupThunk,
  scheduleDeliveryThunk,
  rescheduleDeliveryThunk,
  getFulfillmentDetailsThunk,
  markReadyForPickupThunk,
  completePickupThunk,
  recordDeliveryAttemptThunk,
  completeDeliveryThunk,
  initiateRefundThunk,
  approveRefundThunk,
  completeRefundThunk,
  getRefundDetailsThunk,
  getPendingRefundsThunk,
  cancelRefundThunk,
  clearRefundDetails,
  clearPendingRefunds,
  // Fulfillment actions
  clearFulfillmentDetails,
  setFulfillmentType,

  // Karigar Job Management (NEW)
  getOrderKarigarJobsThunk,
  createOrderKarigarJobThunk,
  createBulkOrderKarigarJobsThunk,
  updateOrderKarigarJobThunk,
  cancelOrderKarigarJobThunk,
  getOrderKarigarSummaryThunk,
  checkKarigarReadinessThunk,
  forceCompleteKarigarJobsThunk,
} from "../thunks/orderThunks";

const initialState = {
  // Orders data
  orders: [],
  order: null,
  draftOrder: null,
  orderStats: null,
  trackingInfo: null,

  // UI state
  loading: false,
  loadingList: false,
  loadingDraft: false,
  loadingApproval: false,
  error: null,

  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    pages: 0,
  },

  // Filters
  filters: {
    status: null,
    branchCode: null,
    fromDate: null,
    toDate: null,
    paymentStatus: null,
    approvalStatus: null,
  },

  // Draft editing state
  draftEditing: {
    canEdit: false,
    requiresApproval: false,
    isLocked: false,
    editHistory: [],
  },

  // Bulk operations
  selectedOrders: [],

  // Pricing & Adjustments State
  pricing: {
    calculated: null,
    loading: false,
    error: null,
    lastCalculatedAt: null,
  },

  adjustments: {
    list: [],
    pending: [],
    active: [],
    loading: false,
    error: null,
  },

  // Exchange Items State
  exchangeItems: {
    list: [],
    accepted: [],
    loading: false,
    error: null,
    calculation: {
      appraisal: null,
      loading: false,
      error: null,
      calculatedAt: null,
    },
  },

  // Fulfillment state
  fulfillmentDetails: null,
  loadingFulfillment: false,
  fulfillmentError: null,

  // Fulfillment UI state
  fulfillmentUI: {
    type: null,
    isScheduling: false,
    isRescheduling: false,
    selectedDate: null,
    selectedTimeSlot: null,
  },

  // Refund Management State
  refund: {
    currentRefund: null, // Current refund being processed
    loading: false,
    error: null,
  },

  pendingRefunds: {
    list: [], // Orders requiring refunds
    loading: false,
    error: null,
    pagination: {
      total: 0,
      page: 1,
      limit: 20,
      pages: 0,
    },
  },

  refundStats: {
    totalPending: 0,
    totalAmount: 0,
    needingApproval: 0,
  },

  karigarJobs: {
    list: [],
    summary: null,
    readiness: null,
    loading: false,
    error: null,
    itemJobMapping: [],
  },

  karigarUI: {
    showAssignmentModal: false,
    selectedItemIndex: null,
    selectedProductId: null,
    showStatusPanel: false,
    showQueueModal: false,
    selectedKarigarId: null,
  },
};

const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    selectOrders: (state, action) => {
      state.selectedOrders = action.payload;
    },
    clearSelectedOrders: (state) => {
      state.selectedOrders = [];
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearExchangeCalculation: (state) => {
      state.exchangeItems.calculation = {
        appraisal: null,
        loading: false,
        error: null,
        calculatedAt: null,
      };
    },
    setPendingRefundsPagination: (state, action) => {
      state.pendingRefunds.pagination = {
        ...state.pendingRefunds.pagination,
        ...action.payload,
      };
    },

    updateRefundInList: (state, action) => {
      const { orderId, refundDetails } = action.payload;
      const orderIndex = state.pendingRefunds.list.findIndex(
        (o) => o.orderId === orderId
      );
      if (orderIndex !== -1) {
        state.pendingRefunds.list[orderIndex].refundDetails = refundDetails;
      }
    },

    openKarigarAssignmentModal: (state, action) => {
      const { itemIndex, productId } = action.payload;
      state.karigarUI.showAssignmentModal = true;
      state.karigarUI.selectedItemIndex = itemIndex;
      state.karigarUI.selectedProductId = productId;
    },

    closeKarigarAssignmentModal: (state) => {
      state.karigarUI.showAssignmentModal = false;
      state.karigarUI.selectedItemIndex = null;
      state.karigarUI.selectedProductId = null;
    },

    openKarigarStatusPanel: (state) => {
      state.karigarUI.showStatusPanel = true;
    },

    closeKarigarStatusPanel: (state) => {
      state.karigarUI.showStatusPanel = false;
    },

    openKarigarQueueModal: (state, action) => {
      state.karigarUI.showQueueModal = true;
      state.karigarUI.selectedKarigarId = action.payload;
    },

    closeKarigarQueueModal: (state) => {
      state.karigarUI.showQueueModal = false;
      state.karigarUI.selectedKarigarId = null;
    },

    clearKarigarUI: (state) => {
      state.karigarUI = {
        showAssignmentModal: false,
        selectedItemIndex: null,
        selectedProductId: null,
        showStatusPanel: false,
        showQueueModal: false,
        selectedKarigarId: null,
      };
    },

    clearKarigarJobs: (state) => {
      state.karigarJobs = {
        list: [],
        summary: null,
        readiness: null,
        loading: false,
        error: null,
        itemJobMapping: [],
      };
    },
  },

  extraReducers: (builder) => {
    // ========================================================================
    // ADMIN THUNKS - CANCEL ORDER
    // ========================================================================
    builder
      .addCase(cancelOrderThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelOrderThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload.data.order;

        const index = state.orders.findIndex(
          (o) => o.orderId === updatedOrder.orderId
        );
        if (index !== -1) {
          state.orders[index] = updatedOrder;
        }

        if (state.order?.orderId === updatedOrder.orderId) {
          state.order = updatedOrder;
        }
      })
      .addCase(cancelOrderThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // ADMIN THUNKS - CREATE ORDER FROM SESSION
    // ========================================================================
    builder
      .addCase(createOrderFromSessionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrderFromSessionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.order = action.payload.data.order;
        state.draftOrder = action.payload.data.order;
        state.draftEditing.requiresApproval =
          action.payload.data.requiresApproval;
      })
      .addCase(createOrderFromSessionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // ADMIN THUNKS - CREATE DIRECT ORDER
    // ========================================================================
    builder
      .addCase(createDirectOrderThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDirectOrderThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.order = action.payload.data.order;
        state.draftOrder = action.payload.data.order;
      })
      .addCase(createDirectOrderThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // ADMIN THUNKS - GET ORDER DRAFT
    // ========================================================================
    builder
      .addCase(getOrderDraftThunk.pending, (state) => {
        state.loadingDraft = true;
        state.error = null;
      })
      .addCase(getOrderDraftThunk.fulfilled, (state, action) => {
        state.loadingDraft = false;
        state.draftOrder = action.payload.data.order;
        state.draftEditing.canEdit = action.payload.data.canEdit;
        state.draftEditing.requiresApproval =
          action.payload.data.requiresApproval;
        state.draftEditing.isLocked =
          action.payload.data.order.priceLock?.isLocked;
        state.draftEditing.editHistory =
          action.payload.data.order.editHistory || [];
      })
      .addCase(getOrderDraftThunk.rejected, (state, action) => {
        state.loadingDraft = false;
        state.error = action.payload;
      });

    // ========================================================================
    // ADMIN THUNKS - UPDATE ORDER ITEMS
    // ========================================================================
    builder
      .addCase(updateOrderItemsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderItemsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;
      })
      .addCase(updateOrderItemsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // PRICING & ADJUSTMENTS - CALCULATE PRICING
    // ========================================================================
    builder
      .addCase(calculateOrderPricingThunk.pending, (state) => {
        state.pricing.loading = true;
        state.pricing.error = null;
      })
      .addCase(calculateOrderPricingThunk.fulfilled, (state, action) => {
        state.pricing.loading = false;
        state.pricing.calculated = action.payload.data.pricing;
        state.pricing.lastCalculatedAt = new Date().toISOString();
      })
      .addCase(calculateOrderPricingThunk.rejected, (state, action) => {
        state.pricing.loading = false;
        state.pricing.error = action.payload;
      });

    // ========================================================================
    // PRICING & ADJUSTMENTS - ADD PRICE ADJUSTMENT
    // ========================================================================
    builder
      .addCase(addPriceAdjustmentThunk.pending, (state) => {
        state.adjustments.loading = true;
        state.adjustments.error = null;
      })
      .addCase(addPriceAdjustmentThunk.fulfilled, (state, action) => {
        state.adjustments.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;

        const adjustment = action.payload.data.adjustment;
        state.adjustments.list.push(adjustment);

        if (adjustment.requiresApproval) {
          state.adjustments.pending.push(adjustment);
        } else {
          state.adjustments.active.push(adjustment);
        }
      })
      .addCase(addPriceAdjustmentThunk.rejected, (state, action) => {
        state.adjustments.loading = false;
        state.adjustments.error = action.payload;
      });

    // ========================================================================
    // PRICING & ADJUSTMENTS - REMOVE PRICE ADJUSTMENT
    // ========================================================================
    builder
      .addCase(removePriceAdjustmentThunk.pending, (state) => {
        state.adjustments.loading = true;
        state.adjustments.error = null;
      })
      .addCase(removePriceAdjustmentThunk.fulfilled, (state, action) => {
        state.adjustments.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;

        if (action.payload.data.order.priceAdjustments) {
          state.adjustments.list = action.payload.data.order.priceAdjustments;
          state.adjustments.pending =
            action.payload.data.order.priceAdjustments.filter(
              (adj) => adj.requiresApproval && adj.status === "PENDING"
            );
          state.adjustments.active =
            action.payload.data.order.priceAdjustments.filter(
              (adj) => adj.status === "ACTIVE"
            );
        }
      })
      .addCase(removePriceAdjustmentThunk.rejected, (state, action) => {
        state.adjustments.loading = false;
        state.adjustments.error = action.payload;
      });

    // ========================================================================
    // PRICING & ADJUSTMENTS - APPROVE PRICE ADJUSTMENT
    // ========================================================================
    builder
      .addCase(approvePriceAdjustmentThunk.pending, (state) => {
        state.adjustments.loading = true;
        state.adjustments.error = null;
      })
      .addCase(approvePriceAdjustmentThunk.fulfilled, (state, action) => {
        state.adjustments.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;

        if (action.payload.data.order.priceAdjustments) {
          state.adjustments.list = action.payload.data.order.priceAdjustments;
          state.adjustments.pending =
            action.payload.data.order.priceAdjustments.filter(
              (adj) => adj.requiresApproval && adj.status === "PENDING"
            );
          state.adjustments.active =
            action.payload.data.order.priceAdjustments.filter(
              (adj) => adj.status === "ACTIVE"
            );
        }
      })
      .addCase(approvePriceAdjustmentThunk.rejected, (state, action) => {
        state.adjustments.loading = false;
        state.adjustments.error = action.payload;
      });

    // ========================================================================
    // PRICING & ADJUSTMENTS - REJECT PRICE ADJUSTMENT
    // ========================================================================
    builder
      .addCase(rejectPriceAdjustmentThunk.pending, (state) => {
        state.adjustments.loading = true;
        state.adjustments.error = null;
      })
      .addCase(rejectPriceAdjustmentThunk.fulfilled, (state, action) => {
        state.adjustments.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;

        if (action.payload.data.order.priceAdjustments) {
          state.adjustments.list = action.payload.data.order.priceAdjustments;
          state.adjustments.pending =
            action.payload.data.order.priceAdjustments.filter(
              (adj) => adj.requiresApproval && adj.status === "PENDING"
            );
          state.adjustments.active =
            action.payload.data.order.priceAdjustments.filter(
              (adj) => adj.status === "ACTIVE"
            );
        }
      })
      .addCase(rejectPriceAdjustmentThunk.rejected, (state, action) => {
        state.adjustments.loading = false;
        state.adjustments.error = action.payload;
      });

    // ========================================================================
    // PRICING & ADJUSTMENTS - LOCK PRICES
    // ========================================================================
    builder
      .addCase(lockPricesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(lockPricesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;
        state.draftEditing.isLocked = true;
      })
      .addCase(lockPricesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // PRICING & ADJUSTMENTS - UNLOCK PRICES
    // ========================================================================
    builder
      .addCase(unlockPricesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unlockPricesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;
        state.draftEditing.isLocked = false;
      })
      .addCase(unlockPricesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // EXCHANGE ITEMS - ADD EXCHANGE ITEM
    // ========================================================================
    builder
      .addCase(addExchangeItemThunk.pending, (state) => {
        state.exchangeItems.loading = true;
        state.exchangeItems.error = null;
      })
      .addCase(addExchangeItemThunk.fulfilled, (state, action) => {
        state.exchangeItems.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;

        if (action.payload.data.order.exchangeItems) {
          state.exchangeItems.list = action.payload.data.order.exchangeItems;
          state.exchangeItems.accepted =
            action.payload.data.order.exchangeItems.filter(
              (item) => item.customerAccepted === true
            );
        }
      })
      .addCase(addExchangeItemThunk.rejected, (state, action) => {
        state.exchangeItems.loading = false;
        state.exchangeItems.error = action.payload;
      });

    // ========================================================================
    // EXCHANGE ITEMS - ADJUST EXCHANGE VALUE
    // ========================================================================
    builder
      .addCase(adjustExchangeValueThunk.pending, (state) => {
        state.exchangeItems.loading = true;
        state.exchangeItems.error = null;
      })
      .addCase(adjustExchangeValueThunk.fulfilled, (state, action) => {
        state.exchangeItems.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;

        if (action.payload.data.order.exchangeItems) {
          state.exchangeItems.list = action.payload.data.order.exchangeItems;
          state.exchangeItems.accepted =
            action.payload.data.order.exchangeItems.filter(
              (item) => item.customerAccepted === true
            );
        }
      })
      .addCase(adjustExchangeValueThunk.rejected, (state, action) => {
        state.exchangeItems.loading = false;
        state.exchangeItems.error = action.payload;
      });

    // ========================================================================
    // EXCHANGE ITEMS - ACCEPT EXCHANGE ITEM
    // ========================================================================
    builder
      .addCase(acceptExchangeItemThunk.pending, (state) => {
        state.exchangeItems.loading = true;
        state.exchangeItems.error = null;
      })
      .addCase(acceptExchangeItemThunk.fulfilled, (state, action) => {
        state.exchangeItems.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;

        if (action.payload.data.order.exchangeItems) {
          state.exchangeItems.list = action.payload.data.order.exchangeItems;
          state.exchangeItems.accepted =
            action.payload.data.order.exchangeItems.filter(
              (item) => item.customerAccepted === true
            );
        }
      })
      .addCase(acceptExchangeItemThunk.rejected, (state, action) => {
        state.exchangeItems.loading = false;
        state.exchangeItems.error = action.payload;
      });

    // ========================================================================
    // EXCHANGE ITEMS - EDIT EXCHANGE ITEM
    // ========================================================================
    builder
      .addCase(editExchangeItemThunk.pending, (state) => {
        state.exchangeItems.loading = true;
        state.exchangeItems.error = null;
      })
      .addCase(editExchangeItemThunk.fulfilled, (state, action) => {
        state.exchangeItems.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;

        if (action.payload.data.order.exchangeItems) {
          state.exchangeItems.list = action.payload.data.order.exchangeItems;
          state.exchangeItems.accepted =
            action.payload.data.order.exchangeItems.filter(
              (item) => item.customerAccepted === true
            );
        }
      })
      .addCase(editExchangeItemThunk.rejected, (state, action) => {
        state.exchangeItems.loading = false;
        state.exchangeItems.error = action.payload;
      });

    // ========================================================================
    // EXCHANGE ITEMS - REMOVE EXCHANGE ITEM
    // ========================================================================
    builder
      .addCase(removeExchangeItemThunk.pending, (state) => {
        state.exchangeItems.loading = true;
        state.exchangeItems.error = null;
      })
      .addCase(removeExchangeItemThunk.fulfilled, (state, action) => {
        state.exchangeItems.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;

        if (action.payload.data.order.exchangeItems) {
          state.exchangeItems.list = action.payload.data.order.exchangeItems;
          state.exchangeItems.accepted =
            action.payload.data.order.exchangeItems.filter(
              (item) => item.customerAccepted === true
            );
        }
      })
      .addCase(removeExchangeItemThunk.rejected, (state, action) => {
        state.exchangeItems.loading = false;
        state.exchangeItems.error = action.payload;
      });

    // ========================================================================
    // EXCHANGE ITEMS - UNACCEPT EXCHANGE ITEM
    // ========================================================================
    builder
      .addCase(unacceptExchangeItemThunk.pending, (state) => {
        state.exchangeItems.loading = true;
        state.exchangeItems.error = null;
      })
      .addCase(unacceptExchangeItemThunk.fulfilled, (state, action) => {
        state.exchangeItems.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;

        if (action.payload.data.order.exchangeItems) {
          state.exchangeItems.list = action.payload.data.order.exchangeItems;
          state.exchangeItems.accepted =
            action.payload.data.order.exchangeItems.filter(
              (item) => item.customerAccepted === true
            );
        }
      })
      .addCase(unacceptExchangeItemThunk.rejected, (state, action) => {
        state.exchangeItems.loading = false;
        state.exchangeItems.error = action.payload;
      });

    // ========================================================================
    // EXCHANGE ITEMS - CALCULATE EXCHANGE VALUE
    // ========================================================================
    builder
      .addCase(calculateExchangeValueThunk.pending, (state) => {
        state.exchangeItems.calculation.loading = true;
        state.exchangeItems.calculation.error = null;
      })
      .addCase(calculateExchangeValueThunk.fulfilled, (state, action) => {
        state.exchangeItems.calculation.loading = false;
        state.exchangeItems.calculation.appraisal =
          action.payload.data.appraisal;
        state.exchangeItems.calculation.calculatedAt = new Date().toISOString();
      })
      .addCase(calculateExchangeValueThunk.rejected, (state, action) => {
        state.exchangeItems.calculation.loading = false;
        state.exchangeItems.calculation.error = action.payload;
      });

    // ========================================================================
    // ORDER NOTES - UPDATE ORDER NOTES
    // ========================================================================
    builder
      .addCase(updateOrderNotesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderNotesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;
      })
      .addCase(updateOrderNotesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // APPROVAL WORKFLOW - SUBMIT FOR APPROVAL
    // ========================================================================
    builder
      .addCase(submitForApprovalThunk.pending, (state) => {
        state.loadingApproval = true;
        state.error = null;
      })
      .addCase(submitForApprovalThunk.fulfilled, (state, action) => {
        state.loadingApproval = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;
      })
      .addCase(submitForApprovalThunk.rejected, (state, action) => {
        state.loadingApproval = false;
        state.error = action.payload;
      });

    // ========================================================================
    // APPROVAL WORKFLOW - APPROVE ORDER
    // ========================================================================
    builder
      .addCase(approveOrderThunk.pending, (state) => {
        state.loadingApproval = true;
        state.error = null;
      })
      .addCase(approveOrderThunk.fulfilled, (state, action) => {
        state.loadingApproval = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;
        state.draftEditing.isLocked = true;
      })
      .addCase(approveOrderThunk.rejected, (state, action) => {
        state.loadingApproval = false;
        state.error = action.payload;
      });

    // ========================================================================
    // APPROVAL WORKFLOW - REJECT ORDER
    // ========================================================================
    builder
      .addCase(rejectOrderThunk.pending, (state) => {
        state.loadingApproval = true;
        state.error = null;
      })
      .addCase(rejectOrderThunk.fulfilled, (state, action) => {
        state.loadingApproval = false;
        state.draftOrder = action.payload.data.order;
        state.order = action.payload.data.order;
      })
      .addCase(rejectOrderThunk.rejected, (state, action) => {
        state.loadingApproval = false;
        state.error = action.payload;
      });

    // ========================================================================
    // APPROVAL WORKFLOW - FINALIZE ORDER
    // ========================================================================
    builder
      .addCase(finalizeOrderThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(finalizeOrderThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.order = action.payload.data.order;
        state.draftOrder = null;
        state.draftEditing.isLocked = true;
      })
      .addCase(finalizeOrderThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // ORDER MANAGEMENT - GET ALL ORDERS FOR ADMIN
    // ========================================================================
    builder
      .addCase(getAllOrdersForAdminThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getAllOrdersForAdminThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.orders = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAllOrdersForAdminThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      });

    // ========================================================================
    // ORDER MANAGEMENT - UPDATE ORDER STATUS
    // ========================================================================
    builder
      .addCase(updateOrderStatusThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderStatusThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload.data;

        const index = state.orders.findIndex(
          (o) => o.orderId === updatedOrder.orderId
        );
        if (index !== -1) {
          state.orders[index] = updatedOrder;
        }

        if (state.order?.orderId === updatedOrder.orderId) {
          state.order = updatedOrder;
        }
      })
      .addCase(updateOrderStatusThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // ORDER MANAGEMENT - UPDATE PAYMENT STATUS
    // ========================================================================
    builder
      .addCase(updatePaymentStatusThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePaymentStatusThunk.fulfilled, (state, action) => {
        state.loading = false;

        const updatedOrder = action.payload.data?.order || action.payload.data;

        if (state.order) {
          state.order = updatedOrder;
        }

        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === updatedOrder?.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex] = updatedOrder;
        }
      })
      .addCase(updatePaymentStatusThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // ORDER MANAGEMENT - GET ORDER STATS
    // ========================================================================
    builder
      .addCase(getOrderStatsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderStatsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.orderStats = action.payload.data;
      })
      .addCase(getOrderStatsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // ORDER MANAGEMENT - GET ORDER BY ID FOR ADMIN
    // ========================================================================
    builder
      .addCase(getOrderByIdForAdminThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderByIdForAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.order = action.payload.data;

        if (action.payload.data.priceAdjustments) {
          state.adjustments.list = action.payload.data.priceAdjustments;
          state.adjustments.pending =
            action.payload.data.priceAdjustments.filter(
              (adj) => adj.requiresApproval && adj.status === "PENDING"
            );
          state.adjustments.active =
            action.payload.data.priceAdjustments.filter(
              (adj) => adj.status === "ACTIVE"
            );
        }

        if (action.payload.data.exchangeItems) {
          state.exchangeItems.list = action.payload.data.exchangeItems;
          state.exchangeItems.accepted =
            action.payload.data.exchangeItems.filter(
              (item) => item.customerAccepted === true
            );
        }
      })
      .addCase(getOrderByIdForAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // FULFILLMENT - SCHEDULE PICKUP
    // ========================================================================
    builder
      .addCase(schedulePickupThunk.pending, (state) => {
        state.loadingFulfillment = true;
        state.fulfillmentError = null;
      })
      .addCase(schedulePickupThunk.fulfilled, (state, action) => {
        state.loadingFulfillment = false;

        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.fulfillment.pickup = action.payload.data.pickupDetails;
        }

        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].fulfillment.pickup =
            action.payload.data.pickupDetails;
        }
      })
      .addCase(schedulePickupThunk.rejected, (state, action) => {
        state.loadingFulfillment = false;
        state.fulfillmentError = action.payload;
      });

    // ========================================================================
    // FULFILLMENT - RESCHEDULE PICKUP
    // ========================================================================
    builder
      .addCase(reschedulePickupThunk.pending, (state) => {
        state.loadingFulfillment = true;
        state.fulfillmentError = null;
      })
      .addCase(reschedulePickupThunk.fulfilled, (state, action) => {
        state.loadingFulfillment = false;

        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.fulfillment.pickup = action.payload.data.pickupDetails;
        }

        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].fulfillment.pickup =
            action.payload.data.pickupDetails;
        }
      })
      .addCase(reschedulePickupThunk.rejected, (state, action) => {
        state.loadingFulfillment = false;
        state.fulfillmentError = action.payload;
      });

    // ========================================================================
    // FULFILLMENT - SCHEDULE DELIVERY
    // ========================================================================
    builder
      .addCase(scheduleDeliveryThunk.pending, (state) => {
        state.loadingFulfillment = true;
        state.fulfillmentError = null;
      })
      .addCase(scheduleDeliveryThunk.fulfilled, (state, action) => {
        state.loadingFulfillment = false;

        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.fulfillment.delivery =
            action.payload.data.deliveryDetails;
        }

        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].fulfillment.delivery =
            action.payload.data.deliveryDetails;
        }
      })
      .addCase(scheduleDeliveryThunk.rejected, (state, action) => {
        state.loadingFulfillment = false;
        state.fulfillmentError = action.payload;
      });

    // ========================================================================
    // FULFILLMENT - RESCHEDULE DELIVERY
    // ========================================================================
    builder
      .addCase(rescheduleDeliveryThunk.pending, (state) => {
        state.loadingFulfillment = true;
        state.fulfillmentError = null;
      })
      .addCase(rescheduleDeliveryThunk.fulfilled, (state, action) => {
        state.loadingFulfillment = false;

        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.fulfillment.delivery =
            action.payload.data.deliveryDetails;
        }

        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].fulfillment.delivery =
            action.payload.data.deliveryDetails;
        }
      })
      .addCase(rescheduleDeliveryThunk.rejected, (state, action) => {
        state.loadingFulfillment = false;
        state.fulfillmentError = action.payload;
      });

    // ========================================================================
    // FULFILLMENT - GET FULFILLMENT DETAILS
    // ========================================================================
    builder
      .addCase(getFulfillmentDetailsThunk.pending, (state) => {
        state.loadingFulfillment = true;
        state.fulfillmentError = null;
      })
      .addCase(getFulfillmentDetailsThunk.fulfilled, (state, action) => {
        state.loadingFulfillment = false;
        state.fulfillmentDetails = action.payload.data.fulfillment;
      })
      .addCase(getFulfillmentDetailsThunk.rejected, (state, action) => {
        state.loadingFulfillment = false;
        state.fulfillmentError = action.payload;
      });

    // ========================================================================
    // FULFILLMENT - MARK READY FOR PICKUP
    // ========================================================================
    builder
      .addCase(markReadyForPickupThunk.pending, (state) => {
        state.loadingFulfillment = true;
        state.fulfillmentError = null;
      })
      .addCase(markReadyForPickupThunk.fulfilled, (state, action) => {
        state.loadingFulfillment = false;

        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.fulfillment.pickup = action.payload.data.pickupDetails;
          state.order.status = "READY";
        }

        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].fulfillment.pickup =
            action.payload.data.pickupDetails;
          state.orders[orderIndex].status = "READY";
        }
      })
      .addCase(markReadyForPickupThunk.rejected, (state, action) => {
        state.loadingFulfillment = false;
        state.fulfillmentError = action.payload;
      });

    // ========================================================================
    // FULFILLMENT - COMPLETE PICKUP
    // ========================================================================
    builder
      .addCase(completePickupThunk.pending, (state) => {
        state.loadingFulfillment = true;
        state.fulfillmentError = null;
      })
      .addCase(completePickupThunk.fulfilled, (state, action) => {
        state.loadingFulfillment = false;

        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.fulfillment.pickup = action.payload.data.pickupDetails;
          state.order.status = "DELIVERED";
        }

        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].fulfillment.pickup =
            action.payload.data.pickupDetails;
          state.orders[orderIndex].status = "DELIVERED";
        }
      })
      .addCase(completePickupThunk.rejected, (state, action) => {
        state.loadingFulfillment = false;
        state.fulfillmentError = action.payload;
      });

    // ========================================================================
    // FULFILLMENT - RECORD DELIVERY ATTEMPT
    // ========================================================================
    builder
      .addCase(recordDeliveryAttemptThunk.pending, (state) => {
        state.loadingFulfillment = true;
        state.fulfillmentError = null;
      })
      .addCase(recordDeliveryAttemptThunk.fulfilled, (state, action) => {
        state.loadingFulfillment = false;

        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.fulfillment.delivery =
            action.payload.data.deliveryDetails;
        }

        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].fulfillment.delivery =
            action.payload.data.deliveryDetails;
        }
      })
      .addCase(recordDeliveryAttemptThunk.rejected, (state, action) => {
        state.loadingFulfillment = false;
        state.fulfillmentError = action.payload;
      });

    // ========================================================================
    // FULFILLMENT - COMPLETE DELIVERY
    // ========================================================================
    builder
      .addCase(completeDeliveryThunk.pending, (state) => {
        state.loadingFulfillment = true;
        state.fulfillmentError = null;
      })
      .addCase(completeDeliveryThunk.fulfilled, (state, action) => {
        state.loadingFulfillment = false;

        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.fulfillment.delivery =
            action.payload.data.deliveryDetails;
          state.order.status = "DELIVERED";
        }

        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].fulfillment.delivery =
            action.payload.data.deliveryDetails;
          state.orders[orderIndex].status = "DELIVERED";
        }
      })
      .addCase(completeDeliveryThunk.rejected, (state, action) => {
        state.loadingFulfillment = false;
        state.fulfillmentError = action.payload;
      });

    // ========================================================================
    // ACTIONS - Clear states
    // ========================================================================
    builder
      .addCase(clearOrderError, (state) => {
        state.error = null;
        state.fulfillmentError = null;
        state.pricing.error = null;
        state.adjustments.error = null;
        state.exchangeItems.error = null;
      })
      .addCase(clearCurrentOrder, (state) => {
        state.order = null;
        state.trackingInfo = null;
        state.adjustments.list = [];
        state.adjustments.pending = [];
        state.adjustments.active = [];
        state.exchangeItems.list = [];
        state.exchangeItems.accepted = [];
      })
      .addCase(clearDraftOrder, (state) => {
        state.draftOrder = null;
        state.draftEditing = {
          canEdit: false,
          requiresApproval: false,
          isLocked: false,
          editHistory: [],
        };
      })
      .addCase(clearPricingData, (state) => {
        state.pricing = {
          calculated: null,
          loading: false,
          error: null,
          lastCalculatedAt: null,
        };
      })
      .addCase(clearAdjustments, (state) => {
        state.adjustments = {
          list: [],
          pending: [],
          active: [],
          loading: false,
          error: null,
        };
      })
      .addCase(clearFulfillmentDetails, (state) => {
        state.fulfillmentDetails = null;
        state.fulfillmentError = null;
        state.fulfillmentUI = {
          type: null,
          isScheduling: false,
          isRescheduling: false,
          selectedDate: null,
          selectedTimeSlot: null,
        };
      })
      .addCase(setFulfillmentType, (state, action) => {
        state.fulfillmentUI.type = action.payload;
      });
    builder
      .addCase(initiateRefundThunk.pending, (state) => {
        state.refund.loading = true;
        state.refund.error = null;
      })
      .addCase(initiateRefundThunk.fulfilled, (state, action) => {
        state.refund.loading = false;
        state.refund.currentRefund = action.payload.data.refundDetails;

        // Update order if it exists in state
        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.refundDetails = action.payload.data.refundDetails;
        }

        // Update in orders list
        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].refundDetails =
            action.payload.data.refundDetails;
        }
      })
      .addCase(initiateRefundThunk.rejected, (state, action) => {
        state.refund.loading = false;
        state.refund.error = action.payload;
      });

    // ========================================================================
    // REFUND MANAGEMENT - APPROVE REFUND
    // ========================================================================
    builder
      .addCase(approveRefundThunk.pending, (state) => {
        state.refund.loading = true;
        state.refund.error = null;
      })
      .addCase(approveRefundThunk.fulfilled, (state, action) => {
        state.refund.loading = false;
        state.refund.currentRefund = action.payload.data;

        // Update order if it exists in state
        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.refundDetails = action.payload.data;
        }

        // Update in orders list
        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].refundDetails = action.payload.data;
        }

        // Remove from pending refunds if present
        state.pendingRefunds.list = state.pendingRefunds.list.filter(
          (order) => order.orderId !== action.payload.data.orderId
        );
      })
      .addCase(approveRefundThunk.rejected, (state, action) => {
        state.refund.loading = false;
        state.refund.error = action.payload;
      });

    // ========================================================================
    // REFUND MANAGEMENT - COMPLETE REFUND
    // ========================================================================
    builder
      .addCase(completeRefundThunk.pending, (state) => {
        state.refund.loading = true;
        state.refund.error = null;
      })
      .addCase(completeRefundThunk.fulfilled, (state, action) => {
        state.refund.loading = false;
        state.refund.currentRefund = action.payload.data;

        // Update order if it exists in state
        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.refundDetails = action.payload.data;
        }

        // Update in orders list
        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].refundDetails = action.payload.data;
        }

        // Remove from pending refunds
        state.pendingRefunds.list = state.pendingRefunds.list.filter(
          (order) => order.orderId !== action.payload.data.orderId
        );
      })
      .addCase(completeRefundThunk.rejected, (state, action) => {
        state.refund.loading = false;
        state.refund.error = action.payload;
      });

    // ========================================================================
    // REFUND MANAGEMENT - GET REFUND DETAILS
    // ========================================================================
    builder
      .addCase(getRefundDetailsThunk.pending, (state) => {
        state.refund.loading = true;
        state.refund.error = null;
      })
      .addCase(getRefundDetailsThunk.fulfilled, (state, action) => {
        state.refund.loading = false;
        state.refund.currentRefund = action.payload.data;
      })
      .addCase(getRefundDetailsThunk.rejected, (state, action) => {
        state.refund.loading = false;
        state.refund.error = action.payload;
      });

    // ========================================================================
    // REFUND MANAGEMENT - GET PENDING REFUNDS
    // ========================================================================
    builder
      .addCase(getPendingRefundsThunk.pending, (state) => {
        state.pendingRefunds.loading = true;
        state.pendingRefunds.error = null;
      })
      .addCase(getPendingRefundsThunk.fulfilled, (state, action) => {
        state.pendingRefunds.loading = false;
        state.pendingRefunds.list = action.payload.data;
        state.pendingRefunds.pagination = action.payload.pagination;

        // Update refund stats
        state.refundStats.totalPending = action.payload.pagination.total;
        state.refundStats.needingApproval = action.payload.data.filter(
          (order) => order.requiresApproval
        ).length;
        state.refundStats.totalAmount = action.payload.data.reduce(
          (sum, order) => sum + (order.refundAmount || 0),
          0
        );
      })
      .addCase(getPendingRefundsThunk.rejected, (state, action) => {
        state.pendingRefunds.loading = false;
        state.pendingRefunds.error = action.payload;
      });

    // ========================================================================
    // REFUND MANAGEMENT - CANCEL REFUND
    // ========================================================================
    builder
      .addCase(cancelRefundThunk.pending, (state) => {
        state.refund.loading = true;
        state.refund.error = null;
      })
      .addCase(cancelRefundThunk.fulfilled, (state, action) => {
        state.refund.loading = false;

        // Update order if it exists in state
        if (state.order?.orderId === action.payload.data.order.orderId) {
          state.order.refundDetails = action.payload.data.order.refundDetails;
        }

        // Update in orders list
        const orderIndex = state.orders.findIndex(
          (o) => o.orderId === action.payload.data.order.orderId
        );
        if (orderIndex !== -1) {
          state.orders[orderIndex].refundDetails =
            action.payload.data.order.refundDetails;
        }

        // Remove from pending refunds
        state.pendingRefunds.list = state.pendingRefunds.list.filter(
          (order) => order.orderId !== action.payload.data.order.orderId
        );
      })
      .addCase(cancelRefundThunk.rejected, (state, action) => {
        state.refund.loading = false;
        state.refund.error = action.payload;
      })
      .addCase(clearRefundDetails, (state) => {
        state.refund.currentRefund = null;
        state.refund.error = null;
      })
      .addCase(clearPendingRefunds, (state) => {
        state.pendingRefunds.list = [];
        state.pendingRefunds.error = null;
        state.pendingRefunds.pagination = {
          total: 0,
          page: 1,
          limit: 20,
          pages: 0,
        };
      });

    // ========================================================================
    // KARIGAR JOB MANAGEMENT - GET ORDER KARIGAR JOBS
    // ========================================================================
    builder
      .addCase(getOrderKarigarJobsThunk.pending, (state) => {
        state.karigarJobs.loading = true;
        state.karigarJobs.error = null;
      })
      .addCase(getOrderKarigarJobsThunk.fulfilled, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.list = action.payload.data.jobs;
        state.karigarJobs.itemJobMapping = action.payload.data.itemJobMapping;

        // Update order's karigarWorkStatus if order is loaded
        if (state.order?.orderId === action.payload.data.orderId) {
          state.order.karigarWorkStatus = action.payload.data.karigarWorkStatus;
        }
      })
      .addCase(getOrderKarigarJobsThunk.rejected, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.error = action.payload;
      });

    // ========================================================================
    // KARIGAR JOB MANAGEMENT - CREATE ORDER KARIGAR JOB
    // ========================================================================
    builder
      .addCase(createOrderKarigarJobThunk.pending, (state) => {
        state.karigarJobs.loading = true;
        state.karigarJobs.error = null;
      })
      .addCase(createOrderKarigarJobThunk.fulfilled, (state, action) => {
        state.karigarJobs.loading = false;

        // Add new job to list
        if (action.payload.data.job) {
          state.karigarJobs.list.push(action.payload.data.job);
        }

        // Update order's karigarWorkStatus
        if (state.order && action.payload.data.karigarWorkStatus) {
          state.order.karigarWorkStatus = action.payload.data.karigarWorkStatus;
        }
      })
      .addCase(createOrderKarigarJobThunk.rejected, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.error = action.payload;
      });

    // ========================================================================
    // KARIGAR JOB MANAGEMENT - CREATE BULK ORDER KARIGAR JOBS
    // ========================================================================
    builder
      .addCase(createBulkOrderKarigarJobsThunk.pending, (state) => {
        state.karigarJobs.loading = true;
        state.karigarJobs.error = null;
      })
      .addCase(createBulkOrderKarigarJobsThunk.fulfilled, (state, action) => {
        state.karigarJobs.loading = false;

        // Add successful jobs to list
        if (action.payload.data.results?.success) {
          action.payload.data.results.success.forEach((result) => {
            // You may want to fetch full job details or add minimal info
            state.karigarJobs.list.push({
              jobId: result.jobId,
              jobNumber: result.jobNumber,
              status: result.status,
              orderItemIndex: result.itemIndex,
            });
          });
        }

        // Update order's karigarWorkStatus
        if (state.order && action.payload.data.karigarWorkStatus) {
          state.order.karigarWorkStatus = action.payload.data.karigarWorkStatus;
        }
      })
      .addCase(createBulkOrderKarigarJobsThunk.rejected, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.error = action.payload;
      });

    // ========================================================================
    // KARIGAR JOB MANAGEMENT - UPDATE ORDER KARIGAR JOB
    // ========================================================================
    builder
      .addCase(updateOrderKarigarJobThunk.pending, (state) => {
        state.karigarJobs.loading = true;
        state.karigarJobs.error = null;
      })
      .addCase(updateOrderKarigarJobThunk.fulfilled, (state, action) => {
        state.karigarJobs.loading = false;

        // Update job in list
        const jobIndex = state.karigarJobs.list.findIndex(
          (j) =>
            j.jobId === action.payload.data.job?.jobId ||
            j._id === action.payload.data.job?.jobId
        );
        if (jobIndex !== -1) {
          state.karigarJobs.list[jobIndex] = {
            ...state.karigarJobs.list[jobIndex],
            ...action.payload.data.job,
          };
        }

        // Update order's karigarWorkStatus
        if (state.order && action.payload.data.karigarWorkStatus) {
          state.order.karigarWorkStatus = action.payload.data.karigarWorkStatus;
        }
      })
      .addCase(updateOrderKarigarJobThunk.rejected, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.error = action.payload;
      });

    // ========================================================================
    // KARIGAR JOB MANAGEMENT - CANCEL ORDER KARIGAR JOB
    // ========================================================================
    builder
      .addCase(cancelOrderKarigarJobThunk.pending, (state) => {
        state.karigarJobs.loading = true;
        state.karigarJobs.error = null;
      })
      .addCase(cancelOrderKarigarJobThunk.fulfilled, (state, action) => {
        state.karigarJobs.loading = false;

        // Update job status in list
        const jobIndex = state.karigarJobs.list.findIndex(
          (j) =>
            j.jobId === action.payload.data.job?.jobId ||
            j._id === action.payload.data.job?.jobId
        );
        if (jobIndex !== -1) {
          state.karigarJobs.list[jobIndex].status = "CANCELLED";
        }

        // Update order's karigarWorkStatus
        if (state.order && action.payload.data.karigarWorkStatus) {
          state.order.karigarWorkStatus = action.payload.data.karigarWorkStatus;
        }
      })
      .addCase(cancelOrderKarigarJobThunk.rejected, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.error = action.payload;
      });

    // ========================================================================
    // KARIGAR JOB MANAGEMENT - GET ORDER KARIGAR SUMMARY
    // ========================================================================
    builder
      .addCase(getOrderKarigarSummaryThunk.pending, (state) => {
        state.karigarJobs.loading = true;
        state.karigarJobs.error = null;
      })
      .addCase(getOrderKarigarSummaryThunk.fulfilled, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.summary = action.payload.data.summary;
        state.karigarJobs.list =
          action.payload.data.jobs || state.karigarJobs.list;

        // Update order's karigarWorkStatus
        if (state.order && action.payload.data.karigarWorkStatus) {
          state.order.karigarWorkStatus = action.payload.data.karigarWorkStatus;
        }
      })
      .addCase(getOrderKarigarSummaryThunk.rejected, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.error = action.payload;
      });

    // ========================================================================
    // KARIGAR JOB MANAGEMENT - CHECK KARIGAR READINESS
    // ========================================================================
    builder
      .addCase(checkKarigarReadinessThunk.pending, (state) => {
        state.karigarJobs.loading = true;
        state.karigarJobs.error = null;
      })
      .addCase(checkKarigarReadinessThunk.fulfilled, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.readiness = action.payload.data.readiness;

        // Update order's karigarWorkStatus
        if (state.order && action.payload.data.karigarWorkStatus) {
          state.order.karigarWorkStatus = action.payload.data.karigarWorkStatus;
        }
      })
      .addCase(checkKarigarReadinessThunk.rejected, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.error = action.payload;
      });

    // ========================================================================
    // KARIGAR JOB MANAGEMENT - FORCE COMPLETE KARIGAR JOBS
    // ========================================================================
    builder
      .addCase(forceCompleteKarigarJobsThunk.pending, (state) => {
        state.karigarJobs.loading = true;
        state.karigarJobs.error = null;
      })
      .addCase(forceCompleteKarigarJobsThunk.fulfilled, (state, action) => {
        state.karigarJobs.loading = false;

        // Update all jobs to COMPLETED status
        if (action.payload.data.results?.success) {
          action.payload.data.results.success.forEach((result) => {
            const jobIndex = state.karigarJobs.list.findIndex(
              (j) => j.jobId === result.jobId || j._id === result.jobId
            );
            if (jobIndex !== -1) {
              state.karigarJobs.list[jobIndex].status = result.newStatus;
            }
          });
        }

        // Update order's karigarWorkStatus
        if (state.order && action.payload.data.karigarWorkStatus) {
          state.order.karigarWorkStatus = action.payload.data.karigarWorkStatus;
        }

        // Update readiness
        state.karigarJobs.readiness = {
          canFulfill: true,
          pendingJobs: [],
          message: "All jobs completed",
        };
      })
      .addCase(forceCompleteKarigarJobsThunk.rejected, (state, action) => {
        state.karigarJobs.loading = false;
        state.karigarJobs.error = action.payload;
      });
  },
});

// Export actions
export const {
  selectOrders,
  clearSelectedOrders,
  setFilters,
  clearFilters,
  setPagination,
  clearExchangeCalculation,
  setPendingRefundsPagination, // ✅ NEW
  updateRefundInList, // ✅ NEW
  openKarigarAssignmentModal,
  closeKarigarAssignmentModal,
  openKarigarStatusPanel,
  closeKarigarStatusPanel,
  openKarigarQueueModal,
  closeKarigarQueueModal,
  clearKarigarUI,
  clearKarigarJobs,
} = orderSlice.actions;

export default orderSlice.reducer;
